import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    Award,
    Building2,
    CheckCircle2,
    Gauge,
    MapPin,
    Route as RouteIcon,
    Target,
    Users,
    Zap
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { Profile } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { statusAtOrAfter } from '../../../src/lib/funnelUtils';
import { useAgendaRealtime } from '../../../src/hooks/useAgendaRealtime';
import MapaCampo, { PontoVisita, TrajetoRota } from './MapaCampo';

// ── Unificação de Visita (adendo Bruno jul/2026) ──
// A TAREFA com `tipo` preenchido é a fonte única da visita: planejadas/concluídas/
// em atraso saem de `tarefas` (mesma fonte da Agenda e do Dashboard de Tarefas).
// `rotas_comerciais`/`rota_paradas` entram só na camada geo: km, trajetos e mapa.

interface NegLead {
    titulo?: string | null;
    cidade?: string | null;
    consumo_medio_kwh?: number | null;
    status: string;
    data_assinatura_termo?: string | null;
}

interface VisitaProd {
    id: number;
    status: string;
    tipo: string | null;
    modalidade?: string | null;
    vencimento: string | null;
    data_conclusao?: string | null;
    responsavel_id?: string | null;
    negociacao_id?: number | null;
    negociacoes?: NegLead | null;
}

interface ParadaGeo {
    id: string;
    status: string;
    posicao: number;
    visitado_em?: string | null;
    negociacao_id?: number | null;
    negociacoes?: {
        titulo?: string | null;
        cidade?: string | null;
        consumo_medio_kwh?: number | null;
        status: string;
        latitude?: number | null;
        longitude?: number | null;
    } | null;
}

interface RotaGeo {
    id: string;
    data: string;
    status: string;
    distancia_total_km?: number | null;
    vendedor_id: string;
    rota_paradas: ParadaGeo[];
}

type Periodo = 'hoje' | 'ontem' | '7d' | '14d' | '30d' | '90d' | 'custom';

const PERIODOS: Array<{ value: Periodo; label: string }> = [
    { value: 'hoje', label: 'Hoje' },
    { value: 'ontem', label: 'Ontem' },
    { value: '7d', label: '7 dias' },
    { value: '14d', label: '14 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
    { value: 'custom', label: 'Personalizado' },
];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const rangeDoPeriodo = (p: Periodo, customIni: string, customFim: string): [string, string] => {
    const hoje = new Date();
    switch (p) {
        case 'hoje': return [iso(hoje), iso(hoje)];
        case 'ontem': { const d = new Date(hoje); d.setDate(d.getDate() - 1); return [iso(d), iso(d)]; }
        case '7d': { const d = new Date(hoje); d.setDate(d.getDate() - 6); return [iso(d), iso(hoje)]; }
        case '14d': { const d = new Date(hoje); d.setDate(d.getDate() - 13); return [iso(d), iso(hoje)]; }
        case '30d': { const d = new Date(hoje); d.setDate(d.getDate() - 29); return [iso(d), iso(hoje)]; }
        case '90d': { const d = new Date(hoje); d.setDate(d.getDate() - 89); return [iso(d), iso(hoje)]; }
        case 'custom': return [customIni || iso(hoje), customFim || iso(hoje)];
    }
};

const fmtNum = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const fmtKwh = (v: number) => `${fmtNum(v)} kWh`;
const fmtPct = (v: number) => `${v.toFixed(1)}%`;

const Kpi: React.FC<{ label: string; value: string; icon: React.ReactNode; tone?: string; hint?: string }> = ({ label, value, icon, tone = 'text-gray-900 dark:text-white', hint }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4" title={hint}>
        <div className="flex items-start justify-between">
            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
            <span className="text-gray-300 dark:text-gray-600">{icon}</span>
        </div>
        <p className={`text-xl font-bold mt-1 ${tone}`}>{value}</p>
    </div>
);

interface ProdutividadeDashboardProps {
    profile: Profile | null;
    profiles: Profile[];
    vendedorSel: string; // '' = eu (vendedor) ou toda a equipe (gestor)
    isManager: boolean;
}

/** Dashboard de Produtividade Externa: contagens pela fonte única (tarefas de visita); geo pelas rotas. */
const ProdutividadeDashboard: React.FC<ProdutividadeDashboardProps> = ({ profile, profiles, vendedorSel, isManager }) => {
    const [periodo, setPeriodo] = useState<Periodo>('30d');
    const [customIni, setCustomIni] = useState(iso(new Date()));
    const [customFim, setCustomFim] = useState(iso(new Date()));
    const [visitas, setVisitas] = useState<VisitaProd[]>([]);
    const [rotas, setRotas] = useState<RotaGeo[]>([]);
    const [loading, setLoading] = useState(true);

    const [ini, fim] = rangeDoPeriodo(periodo, customIni, customFim);
    const escopoEquipe = isManager && !vendedorSel;

    // Fonte única: atividades agendadas do período, paginadas
    // (o Supabase devolve no máx. 1000 linhas por request — 90d passa disso).
    // Sem filtro de tipo: conclusão criada direto no Kanban (tipo NULL) também conta.
    // Escopo equipe fica restrito aos papéis de venda p/ não varrer tarefas de outros setores.
    const fetchVisitas = useCallback(async () => {
        if (!profile) return;
        const vendedorIds = profiles
            .filter(p => ['vendedor', 'vendedor_parceiro', 'parceiro'].includes((p as any).role))
            .map(p => String(p.id));
        const PAGE = 1000;
        const todas: VisitaProd[] = [];
        for (let pagina = 0; pagina < 10; pagina++) {
            let query = supabase
                .from('tarefas')
                .select('id, status, tipo, modalidade, vencimento, data_conclusao, responsavel_id, negociacao_id, negociacoes(titulo, cidade, consumo_medio_kwh, status, data_assinatura_termo)')
                .neq('status', 'cancelado')
                .gte('vencimento', ini)
                .lte('vencimento', fim)
                .order('id')
                .range(pagina * PAGE, pagina * PAGE + PAGE - 1);
            if (vendedorSel) query = query.eq('responsavel_id', vendedorSel);
            else if (!isManager) query = query.eq('responsavel_id', profile.id);
            else query = query.in('responsavel_id', vendedorIds.length ? vendedorIds : [profile.id]);
            const { data, error } = await query;
            if (error) throw error;
            todas.push(...((data || []) as unknown as VisitaProd[]));
            if (!data || data.length < PAGE) break;
        }
        setVisitas(todas);
    }, [profile?.id, profiles, vendedorSel, isManager, ini, fim]);

    // Camada geo: rotas do período (km percorrida, trajetos e pontos do mapa)
    const fetchRotas = useCallback(async () => {
        if (!profile) return;
        let query = supabase
            .from('rotas_comerciais')
            .select('id, data, status, distancia_total_km, vendedor_id, rota_paradas(id, status, posicao, visitado_em, negociacao_id, negociacoes(titulo, cidade, consumo_medio_kwh, status, latitude, longitude))')
            .gte('data', ini)
            .lte('data', fim)
            .neq('status', 'cancelada');
        if (vendedorSel) query = query.eq('vendedor_id', vendedorSel);
        else if (!isManager) query = query.eq('vendedor_id', profile.id);
        const { data, error } = await query;
        if (error) throw error;
        setRotas((data || []) as unknown as RotaGeo[]);
    }, [profile?.id, vendedorSel, isManager, ini, fim]);

    useEffect(() => {
        const fetchAll = async () => {
            if (!profile) return;
            setLoading(true);
            try {
                await Promise.all([fetchVisitas(), fetchRotas()]);
            } catch (err) {
                console.error('Erro ao carregar produtividade:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [fetchVisitas, fetchRotas]);

    // Critério de aceite: concluir a visita em qualquer tela reflete aqui no mesmo instante.
    useAgendaRealtime({
        enabled: !!profile && !loading,
        onTarefasChange: () => { fetchVisitas().catch(console.error); },
        onRotasChange: () => { fetchRotas().catch(console.error); },
    });

    const stats = useMemo(() => {
        const hojeIso = iso(new Date());
        const realizadasArr = visitas.filter(v => v.status === 'concluido');
        const planejadas = visitas.length;
        const realizadas = realizadasArr.length;
        const emAtraso = visitas.filter(v => v.status !== 'concluido' && (v.vencimento || '') < hojeIso).length;
        const taxaExecucao = planejadas > 0 ? (realizadas / planejadas) * 100 : 0;
        const km = rotas
            .filter(r => r.status === 'concluida' || r.status === 'em_execucao')
            .reduce((s, r) => s + Number(r.distancia_total_km || 0), 0);

        // Leads visitados (únicos) e seus volumes — visitas concluídas, com ou sem geo
        const leadsMap = new Map<number, NegLead | null | undefined>();
        for (const v of realizadasArr) {
            if (v.negociacao_id && v.negociacoes && !leadsMap.has(v.negociacao_id)) {
                leadsMap.set(v.negociacao_id, v.negociacoes);
            }
        }
        const leadsVisitados = leadsMap.size;
        let kwhOriginado = 0, kwhConvertido = 0, leadsAvancaram = 0;
        for (const neg of leadsMap.values()) {
            const kwh = Number(neg?.consumo_medio_kwh || 0);
            kwhOriginado += kwh;
            const assinado = neg ? (statusAtOrAfter(neg.status, 'termo_assinado') || (neg.status === 'em_pausa' && !!neg.data_assinatura_termo)) : false;
            if (assinado) kwhConvertido += kwh;
            if (neg && (statusAtOrAfter(neg.status, 'simulacao_realizada') || assinado)) leadsAvancaram++;
        }
        const conversaoPorVisita = leadsVisitados > 0 ? (leadsAvancaram / leadsVisitados) * 100 : 0;

        // Cidades visitadas
        const cidades: Record<string, number> = {};
        for (const v of realizadasArr) {
            const c = v.negociacoes?.cidade?.trim();
            if (c) cidades[c] = (cidades[c] || 0) + 1;
        }
        const cidadesList = Object.entries(cidades).sort((a, b) => b[1] - a[1]);

        // Ranking por vendedor (escopo equipe) — contagens das tarefas, km das rotas
        const porVendedor: Record<string, { realizadas: number; planejadas: number; km: number; kwh: number }> = {};
        for (const v of visitas) {
            const vid = String(v.responsavel_id || '');
            if (!vid) continue;
            if (!porVendedor[vid]) porVendedor[vid] = { realizadas: 0, planejadas: 0, km: 0, kwh: 0 };
            porVendedor[vid].planejadas++;
            if (v.status === 'concluido') {
                porVendedor[vid].realizadas++;
                porVendedor[vid].kwh += Number(v.negociacoes?.consumo_medio_kwh || 0);
            }
        }
        for (const r of rotas) {
            if (r.status !== 'concluida' && r.status !== 'em_execucao') continue;
            const vid = String(r.vendedor_id);
            if (!porVendedor[vid]) porVendedor[vid] = { realizadas: 0, planejadas: 0, km: 0, kwh: 0 };
            porVendedor[vid].km += Number(r.distancia_total_km || 0);
        }
        const ranking = Object.entries(porVendedor)
            .map(([id, v]) => ({ id, ...v, taxa: v.planejadas > 0 ? (v.realizadas / v.planejadas) * 100 : 0 }))
            .sort((a, b) => b.realizadas - a.realizadas)
            .slice(0, 8);

        return { planejadas, realizadas, emAtraso, taxaExecucao, km, leadsVisitados, kwhOriginado, kwhConvertido, conversaoPorVisita, cidadesList, ranking };
    }, [visitas, rotas]);

    const nome = (id: string) => profiles.find(p => String(p.id) === id)?.full_name?.split(' ').slice(0, 2).join(' ') || '—';

    // ---------- Mapa de campo e trajetos (camada geo — rotas) ----------
    const mapa = useMemo(() => {
        const pontos: PontoVisita[] = [];
        const trajetos: TrajetoRota[] = [];
        for (const r of rotas) {
            const ordenadas = [...r.rota_paradas].sort((a, b) => a.posicao - b.posicao);
            const coords: Array<[number, number]> = [];
            for (const p of ordenadas) {
                const neg = p.negociacoes;
                if (neg?.latitude && neg?.longitude) coords.push([Number(neg.latitude), Number(neg.longitude)]);
                if (p.status !== 'visitado' || !neg) continue;
                if (neg.latitude && neg.longitude) {
                    pontos.push({
                        paradaId: p.id,
                        lat: Number(neg.latitude),
                        lng: Number(neg.longitude),
                        titulo: neg.titulo || 'Lead',
                        vendedorNome: nome(r.vendedor_id),
                        dataVisita: p.visitado_em,
                        statusLead: neg.status,
                        kwh: Number(neg.consumo_medio_kwh || 0),
                        cidade: neg.cidade,
                    });
                }
            }
            if ((r.status === 'concluida' || r.status === 'em_execucao') && coords.length >= 2) {
                trajetos.push({ rotaId: r.id, data: r.data, vendedorNome: nome(r.vendedor_id), coords });
            }
        }
        return { pontos, trajetos };
    }, [rotas, profiles]);

    // ---------- Visão geocomercial (fonte única: visitas concluídas nas tarefas) ----------
    const geocomercial = useMemo(() => {
        // Por cidade: visitas, leads únicos, kWh e quem avançou no funil
        const regioes: Record<string, { visitas: number; leads: Set<number>; kwh: number; avancaram: Set<number> }> = {};
        const replayLeads = new Map<number, NegLead | null | undefined>();

        for (const v of visitas) {
            if (v.status !== 'concluido') continue;
            const neg = v.negociacoes;
            if (v.negociacao_id && neg && !replayLeads.has(v.negociacao_id)) replayLeads.set(v.negociacao_id, neg);
            const cid = neg?.cidade?.trim();
            if (!cid) continue;
            if (!regioes[cid]) regioes[cid] = { visitas: 0, leads: new Set(), kwh: 0, avancaram: new Set() };
            regioes[cid].visitas++;
            if (v.negociacao_id && neg && !regioes[cid].leads.has(v.negociacao_id)) {
                regioes[cid].leads.add(v.negociacao_id);
                regioes[cid].kwh += Number(neg.consumo_medio_kwh || 0);
                if (statusAtOrAfter(neg.status, 'simulacao_realizada') || (neg.status === 'em_pausa' && !!neg.data_assinatura_termo)) {
                    regioes[cid].avancaram.add(v.negociacao_id);
                }
            }
        }

        const regioesList = Object.entries(regioes)
            .map(([cidade, v]) => ({
                cidade,
                visitas: v.visitas,
                leads: v.leads.size,
                kwh: v.kwh,
                conversao: v.leads.size > 0 ? (v.avancaram.size / v.leads.size) * 100 : 0,
            }))
            .sort((a, b) => b.kwh - a.kwh);

        let simulacoes = 0, termosEnviados = 0, assinados = 0;
        for (const neg of replayLeads.values()) {
            if (!neg) continue;
            const efetivo = (alvo: string) => statusAtOrAfter(neg.status, alvo) || (neg.status === 'em_pausa' && !!neg.data_assinatura_termo && statusAtOrAfter('termo_assinado', alvo));
            if (efetivo('simulacao_realizada')) simulacoes++;
            if (efetivo('termo_enviado')) termosEnviados++;
            if (efetivo('termo_assinado')) assinados++;
        }

        const cidadeMaiorPotencial = regioesList[0] || null;
        const cidadeMaiorConversao = regioesList.filter(r => r.leads >= 2).sort((a, b) => b.conversao - a.conversao)[0] || null;

        return { regioesList, simulacoes, termosEnviados, assinados, cidadeMaiorPotencial, cidadeMaiorConversao };
    }, [visitas]);

    return (
        <div className="space-y-4">
            {/* Filtros temporais */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-2">
                <Gauge size={15} className="text-gray-400 ml-1" />
                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 mr-1">
                    Produtividade Externa {escopoEquipe ? '· Toda a Equipe' : vendedorSel ? `· ${nome(vendedorSel)}` : ''}
                </span>
                <div className="flex items-center gap-1 flex-wrap ml-auto">
                    {PERIODOS.map(p => (
                        <button
                            key={p.value}
                            onClick={() => setPeriodo(p.value)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${periodo === p.value
                                ? 'bg-primary text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        >
                            {p.label}
                        </button>
                    ))}
                    {periodo === 'custom' && (
                        <span className="flex items-center gap-1.5 ml-1">
                            <input type="date" value={customIni} onChange={e => setCustomIni(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none" />
                            <span className="text-xs text-gray-400">→</span>
                            <input type="date" value={customFim} onChange={e => setCustomFim(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none" />
                        </span>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><LoadingSpinner label="Calculando produtividade..." size="sm" /></div>
            ) : (
                <>
                    {/* KPIs — planejadas/concluídas/atraso saem da MESMA fonte da Agenda e das Tarefas */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        <Kpi label="Visitas Planejadas" value={fmtNum(stats.planejadas)} icon={<Target size={18} />} hint="Visitas agendadas no período (fonte única — mesma da Agenda e do Dashboard de Tarefas)" />
                        <Kpi label="Visitas Realizadas" value={fmtNum(stats.realizadas)} icon={<CheckCircle2 size={18} />} tone="text-green-600 dark:text-green-400" hint="Visitas concluídas — pela rota, pela tela de Tarefas ou pelo lead" />
                        <Kpi label="Em Atraso" value={fmtNum(stats.emAtraso)} icon={<AlertTriangle size={18} />} tone={stats.emAtraso > 0 ? 'text-red-500' : 'text-gray-900 dark:text-white'} hint="Visitas planejadas para datas passadas e ainda não concluídas" />
                        <Kpi label="Taxa de Execução" value={fmtPct(stats.taxaExecucao)} icon={<Gauge size={18} />} tone={stats.taxaExecucao >= 70 ? 'text-green-600 dark:text-green-400' : stats.taxaExecucao >= 40 ? 'text-amber-500' : 'text-red-500'} hint="Realizadas ÷ planejadas" />
                        <Kpi label="Km Percorrida" value={`${fmtNum(stats.km)} km`} icon={<RouteIcon size={18} />} tone="text-blue-600 dark:text-blue-400" hint="Distância estimada das rotas executadas" />
                        <Kpi label="Cidades Visitadas" value={fmtNum(stats.cidadesList.length)} icon={<Building2 size={18} />} hint="Municípios com visitas realizadas" />
                        <Kpi label="Leads Visitados" value={fmtNum(stats.leadsVisitados)} icon={<Users size={18} />} hint="Leads únicos visitados no período" />
                        <Kpi label="Conversão por Visita" value={fmtPct(stats.conversaoPorVisita)} icon={<Award size={18} />} tone="text-purple-600 dark:text-purple-400" hint="Leads visitados que avançaram para simulação ou além" />
                        <Kpi label="kWh Originado" value={fmtKwh(stats.kwhOriginado)} icon={<Zap size={18} />} tone="text-amber-500" hint="Volume energético dos leads visitados" />
                        <Kpi label="kWh Convertido" value={fmtKwh(stats.kwhConvertido)} icon={<Zap size={18} />} tone="text-green-600 dark:text-green-400" hint="Volume dos leads visitados com termo assinado" />
                    </div>

                    {/* Cidades + Ranking */}
                    <div className={`grid grid-cols-1 ${escopoEquipe ? 'lg:grid-cols-2' : ''} gap-4`}>
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <MapPin size={15} className="text-gray-400" /> Visitas por Cidade
                            </h2>
                            {stats.cidadesList.length === 0 ? (
                                <p className="text-xs text-gray-400">Nenhuma visita realizada no período.</p>
                            ) : (
                                <div className="space-y-2">
                                    {stats.cidadesList.slice(0, 8).map(([cidade, qtd]) => {
                                        const pct = stats.realizadas > 0 ? (qtd / stats.realizadas) * 100 : 0;
                                        return (
                                            <div key={cidade}>
                                                <div className="flex justify-between text-xs mb-0.5">
                                                    <span className="text-gray-600 dark:text-gray-300">{cidade}</span>
                                                    <span className="font-bold text-gray-800 dark:text-gray-100">{qtd}</span>
                                                </div>
                                                <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                                    <div className="h-full rounded-full bg-blue-400" style={{ width: `${pct}%` }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {escopoEquipe && (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                    <Award size={15} className="text-amber-500" /> Ranking de Campo
                                </h2>
                                {stats.ranking.length === 0 ? (
                                    <p className="text-xs text-gray-400">Nenhuma visita no período.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {stats.ranking.map((r, i) => (
                                            <div key={r.id} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                                                <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white shrink-0 ${i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-400' : 'bg-gray-300 dark:bg-gray-600'}`}>{i + 1}</span>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">{nome(r.id)}</p>
                                                    <p className="text-[10px] text-gray-400">{r.realizadas}/{r.planejadas} visitas · {fmtPct(r.taxa)} · ~{fmtNum(r.km)} km</p>
                                                </div>
                                                <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400 shrink-0">{fmtKwh(r.kwh)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mapa de Inteligência Comercial + trajetos (camada geo) */}
                    <MapaCampo pontos={mapa.pontos} trajetos={mapa.trajetos} />

                    {/* Dashboard Geocomercial */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Dashboard Geocomercial</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Resultado do funil a partir dos leads visitados no período</p>

                        {/* Replay: indicadores do período */}
                        <div className="flex flex-wrap gap-2 mb-4">
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Visitas: <b>{fmtNum(stats.realizadas)}</b></span>
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Leads trabalhados: <b>{fmtNum(stats.leadsVisitados)}</b></span>
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">Simulações: <b>{fmtNum(geocomercial.simulacoes)}</b></span>
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Termos enviados: <b>{fmtNum(geocomercial.termosEnviados)}</b></span>
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400">Contratos assinados: <b>{fmtNum(geocomercial.assinados)}</b></span>
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">kWh movimentado: <b>{fmtKwh(stats.kwhOriginado)}</b></span>
                        </div>

                        {/* Destaques */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                            <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3">
                                <p className="text-[10px] font-bold text-amber-600/70 dark:text-amber-400/70 uppercase">Vendedor mais ativo</p>
                                <p className="text-sm font-bold text-amber-700 dark:text-amber-300 truncate">{stats.ranking[0] ? nome(stats.ranking[0].id) : '—'}</p>
                                {stats.ranking[0] && <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70">{stats.ranking[0].realizadas} visitas realizadas</p>}
                            </div>
                            <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3">
                                <p className="text-[10px] font-bold text-blue-600/70 dark:text-blue-400/70 uppercase">Cidade com maior potencial</p>
                                <p className="text-sm font-bold text-blue-700 dark:text-blue-300 truncate">{geocomercial.cidadeMaiorPotencial?.cidade || '—'}</p>
                                {geocomercial.cidadeMaiorPotencial && <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70">{fmtKwh(geocomercial.cidadeMaiorPotencial.kwh)} em leads visitados</p>}
                            </div>
                            <div className="rounded-xl bg-green-50 dark:bg-green-900/20 p-3">
                                <p className="text-[10px] font-bold text-green-600/70 dark:text-green-400/70 uppercase">Cidade com maior conversão</p>
                                <p className="text-sm font-bold text-green-700 dark:text-green-300 truncate">{geocomercial.cidadeMaiorConversao?.cidade || '—'}</p>
                                {geocomercial.cidadeMaiorConversao && <p className="text-[10px] text-green-600/70 dark:text-green-400/70">{fmtPct(geocomercial.cidadeMaiorConversao.conversao)} dos leads visitados avançaram</p>}
                            </div>
                        </div>

                        {/* Por região */}
                        {geocomercial.regioesList.length === 0 ? (
                            <p className="text-xs text-gray-400">Sem visitas com cidade no período.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                            <th className="py-2 pr-3 font-semibold">Região</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Visitas</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Leads</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Conversão</th>
                                            <th className="py-2 font-semibold text-right">kWh</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {geocomercial.regioesList.slice(0, 10).map(r => (
                                            <tr key={r.cidade} className="border-b border-gray-50 dark:border-gray-700/50">
                                                <td className="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">{r.cidade}</td>
                                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{r.visitas}</td>
                                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{r.leads}</td>
                                                <td className={`py-2 pr-3 text-right font-bold ${r.conversao >= 50 ? 'text-green-600 dark:text-green-400' : r.conversao >= 20 ? 'text-amber-500' : 'text-red-500'}`}>{fmtPct(r.conversao)}</td>
                                                <td className="py-2 text-right text-gray-600 dark:text-gray-300">{fmtKwh(r.kwh)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ProdutividadeDashboard;
